import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type RosterDayType = "work_day" | "weekend" | "holiday";

export type RosterCellSource = "roster" | "assignment" | "calendar" | "employee_off" | "none";

export type RosterDayMeta = {
  date: string;
  dayOfMonth: number;
  dayOfWeek: number;
  dayType: RosterDayType;
  holidayTitle: string | null;
};

export type RosterCell = {
  workShiftId: string | null;
  workShiftName: string | null;
  workShiftSlug: string | null;
  startTime: string | null;
  endTime: string | null;
  source: RosterCellSource;
};

export type RosterEmployeeRow = {
  id: string;
  employeeCode: string;
  fullName: string;
  departmentId: string | null;
  departmentName: string;
  cells: Record<string, RosterCell>;
};

export type RosterWorkShiftOption = {
  id: string;
  name: string;
  slug: string;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number;
  crossesMidnight: boolean;
  active: boolean;
};

export type RosterMonthData = {
  year: number;
  month: number;
  monthLabel: string;
  dateFrom: string;
  dateTo: string;
  days: RosterDayMeta[];
  employees: RosterEmployeeRow[];
  workShifts: RosterWorkShiftOption[];
};

type RosterCellApi = {
  work_shift_id?: number | string | null;
  work_shift_name?: string | null;
  work_shift_slug?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  source?: string | null;
};

type RosterDayApi = {
  date?: string;
  day_of_month?: number;
  day_of_week?: number;
  day_type?: string;
  holiday_title?: string | null;
};

type RosterEmployeeApi = {
  id?: number | string;
  employee_code?: string | null;
  full_name?: string | null;
  department_id?: number | string | null;
  department_name?: string | null;
  cells?: Record<string, RosterCellApi>;
};

type RosterWorkShiftApi = {
  id?: number | string;
  name?: string | null;
  slug?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  break_minutes?: number | string | null;
  crosses_midnight?: boolean | null;
  active?: boolean | null;
};

type RosterMonthApi = {
  year?: number;
  month?: number;
  month_label?: string;
  date_from?: string;
  date_to?: string;
  days?: RosterDayApi[];
  employees?: RosterEmployeeApi[];
  work_shifts?: RosterWorkShiftApi[];
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function mapCell(api: RosterCellApi | undefined): RosterCell {
  const sourceRaw = str(api?.source);
  const source: RosterCellSource =
    sourceRaw === "roster" ||
    sourceRaw === "assignment" ||
    sourceRaw === "calendar" ||
    sourceRaw === "employee_off" ||
    sourceRaw === "none"
      ? sourceRaw
      : "none";

  return {
    workShiftId: api?.work_shift_id == null || api?.work_shift_id === "" ? null : str(api.work_shift_id),
    workShiftName: str(api?.work_shift_name) || null,
    workShiftSlug: str(api?.work_shift_slug) || null,
    startTime: str(api?.start_time) || null,
    endTime: str(api?.end_time) || null,
    source,
  };
}

function mapMonth(api: RosterMonthApi): RosterMonthData {
  const days: RosterDayMeta[] = Array.isArray(api.days)
    ? api.days.map((d) => {
        const dayTypeRaw = str(d.day_type);
        const dayType: RosterDayType =
          dayTypeRaw === "weekend" || dayTypeRaw === "holiday" ? dayTypeRaw : "work_day";

        return {
          date: str(d.date),
          dayOfMonth: Number(d.day_of_month) || 0,
          dayOfWeek: Number(d.day_of_week) || 0,
          dayType,
          holidayTitle: str(d.holiday_title) || null,
        };
      })
    : [];

  const employees: RosterEmployeeRow[] = Array.isArray(api.employees)
    ? api.employees.map((e) => {
        const cells: Record<string, RosterCell> = {};
        if (e.cells && typeof e.cells === "object") {
          for (const [date, cell] of Object.entries(e.cells)) {
            cells[date] = mapCell(cell);
          }
        }

        return {
          id: str(e.id),
          employeeCode: str(e.employee_code),
          fullName: str(e.full_name),
          departmentId: e.department_id == null || e.department_id === "" ? null : str(e.department_id),
          departmentName: str(e.department_name),
          cells,
        };
      })
    : [];

  const workShifts: RosterWorkShiftOption[] = Array.isArray(api.work_shifts)
    ? api.work_shifts.map((s) => ({
        id: str(s.id),
        name: str(s.name),
        slug: str(s.slug),
        startTime: str(s.start_time) || null,
        endTime: str(s.end_time) || null,
        breakMinutes: Number(s.break_minutes) || 0,
        crossesMidnight: Boolean(s.crosses_midnight),
        active: s.active !== false,
      }))
    : [];

  return {
    year: Number(api.year) || new Date().getFullYear(),
    month: Number(api.month) || new Date().getMonth() + 1,
    monthLabel: str(api.month_label),
    dateFrom: str(api.date_from),
    dateTo: str(api.date_to),
    days,
    employees,
    workShifts,
  };
}

export function rosterApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const msg = error.response?.data?.message;
    if (typeof msg === "string" && msg.trim()) return msg;
    if (error.response?.status === 403) return "You do not have permission for this roster action.";
    if (error.response?.status === 422) return "Invalid roster data. Check your entries and try again.";
  }
  return "Roster request failed. Please try again.";
}

export async function fetchRosterMonth(params: {
  year: number;
  month: number;
  departmentId?: string;
  employeeId?: string;
}): Promise<RosterMonthData> {
  const response = await api.get<{ data?: RosterMonthApi }>("/api/v1/rosters/month", {
    params: {
      year: params.year,
      month: params.month,
      department_id: params.departmentId || undefined,
      employee_id: params.employeeId || undefined,
    },
  });

  return mapMonth(response.data?.data ?? {});
}

export async function saveRosterEntries(
  entries: Array<{ employeeId: string; rosterDate: string; workShiftId: string | null }>
): Promise<{ message: string; saved: number }> {
  const response = await api.put<{ message?: string; data?: { saved?: number } }>("/api/v1/rosters/entries", {
    entries: entries.map((e) => ({
      employee_id: Number(e.employeeId),
      roster_date: e.rosterDate,
      work_shift_id: e.workShiftId ? Number(e.workShiftId) : null,
    })),
  });

  return {
    message: response.data?.message || "Roster saved.",
    saved: Number(response.data?.data?.saved) || 0,
  };
}

export async function fillRosterFromAssignments(params: {
  year: number;
  month: number;
  departmentId?: string;
  employeeId?: string;
  overwrite?: boolean;
}): Promise<{ message: string; filled: number; skipped: number }> {
  const response = await api.post<{ message?: string; data?: { filled?: number; skipped?: number } }>(
    "/api/v1/rosters/fill-from-assignments",
    {
      year: params.year,
      month: params.month,
      department_id: params.departmentId ? Number(params.departmentId) : undefined,
      employee_id: params.employeeId ? Number(params.employeeId) : undefined,
      overwrite: params.overwrite ?? false,
    }
  );

  return {
    message: response.data?.message || "Roster filled.",
    filled: Number(response.data?.data?.filled) || 0,
    skipped: Number(response.data?.data?.skipped) || 0,
  };
}

export function rosterCellTimeLabel(cell: Pick<RosterCell, "startTime" | "endTime">): string | null {
  if (cell.startTime && cell.endTime) return `${cell.startTime} – ${cell.endTime}`;
  return cell.startTime || cell.endTime || null;
}

export function rosterCellKey(employeeId: string, date: string): string {
  return `${employeeId}|${date}`;
}

export function previousCalendarMonth(year: number, month: number): { year: number; month: number } {
  const d = new Date(year, month - 2, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

export type RosterPendingEntry = {
  employeeId: string;
  rosterDate: string;
  workShiftId: string | null;
};

/** Entries that still need persisting (unsaved preview cells, pending edits, or drift from saved roster). */
export function buildSaveEntriesForEmployee(
  row: RosterEmployeeRow,
  days: RosterDayMeta[],
  pending: Map<string, RosterPendingEntry>
): RosterPendingEntry[] {
  const entries: RosterPendingEntry[] = [];

  for (const day of days) {
    const key = rosterCellKey(row.id, day.date);
    const pendingChange = pending.get(key);
    const saved = row.cells[day.date];

    const workShiftId = pendingChange ? pendingChange.workShiftId : (saved?.workShiftId ?? null);
    const savedShiftId = saved?.workShiftId ?? null;
    const hasPending = pendingChange !== undefined;
    const isPersisted = saved?.source === "roster";

    if (!hasPending && isPersisted && savedShiftId === workShiftId) {
      continue;
    }

    entries.push({
      employeeId: row.id,
      rosterDate: day.date,
      workShiftId,
    });
  }

  return entries;
}

export function buildSaveEntriesForEmployees(
  employees: RosterEmployeeRow[],
  days: RosterDayMeta[],
  pending: Map<string, RosterPendingEntry>,
  employeeIds?: Set<string>
): RosterPendingEntry[] {
  const entries: RosterPendingEntry[] = [];
  for (const row of employees) {
    if (employeeIds && !employeeIds.has(row.id)) continue;
    entries.push(...buildSaveEntriesForEmployee(row, days, pending));
  }
  return entries;
}

/** Map saved roster cells from previous month onto current month work days (by day-of-month). */
export function buildCopyFromPreviousMonthEntries(params: {
  employeeId: string;
  currentDays: RosterDayMeta[];
  currentCells: Record<string, RosterCell>;
  previousDays: RosterDayMeta[];
  previousCells: Record<string, RosterCell>;
}): RosterPendingEntry[] {
  const prevByDayNum = new Map<number, RosterCell>();
  for (const day of params.previousDays) {
    const cell = params.previousCells[day.date];
    if (cell?.source === "roster") {
      prevByDayNum.set(day.dayOfMonth, cell);
    }
  }

  const entries: RosterPendingEntry[] = [];
  for (const day of params.currentDays) {
    if (day.dayType === "holiday") continue;
    if (!prevByDayNum.has(day.dayOfMonth)) continue;

    const targetShiftId = prevByDayNum.get(day.dayOfMonth)!.workShiftId;
    const originalId = params.currentCells[day.date]?.workShiftId ?? null;
    if (originalId === targetShiftId) continue;

    entries.push({
      employeeId: params.employeeId,
      rosterDate: day.date,
      workShiftId: targetShiftId,
    });
  }

  return entries;
}

export const ROSTER_WEEKDAY_HEADERS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;

export type RosterCalendarWeek = {
  index: number;
  label: string;
  days: Array<RosterDayMeta | null>;
};

/** Split month days into Mon–Sun calendar weeks (7 slots per row). */
export function buildCalendarWeeksMondayFirst(days: RosterDayMeta[]): RosterCalendarWeek[] {
  if (days.length === 0) return [];

  const weeks: RosterCalendarWeek[] = [];
  let current: Array<RosterDayMeta | null> = [];

  const lead = (days[0].dayOfWeek + 6) % 7;
  for (let i = 0; i < lead; i++) {
    current.push(null);
  }

  for (const day of days) {
    current.push(day);
    if (current.length === 7) {
      weeks.push(makeWeek(weeks.length, current));
      current = [];
    }
  }

  if (current.length > 0) {
    while (current.length < 7) current.push(null);
    weeks.push(makeWeek(weeks.length, current));
  }

  return weeks;
}

/** Split month days into Sun–Sat calendar weeks (7 slots per row). */
export function buildCalendarWeeks(days: RosterDayMeta[]): RosterCalendarWeek[] {
  if (days.length === 0) return [];

  const weeks: RosterCalendarWeek[] = [];
  let current: Array<RosterDayMeta | null> = [];

  for (let i = 0; i < days[0].dayOfWeek; i++) {
    current.push(null);
  }

  for (const day of days) {
    current.push(day);
    if (current.length === 7) {
      weeks.push(makeWeek(weeks.length, current));
      current = [];
    }
  }

  if (current.length > 0) {
    while (current.length < 7) current.push(null);
    weeks.push(makeWeek(weeks.length, current));
  }

  return weeks;
}

function makeWeek(index: number, days: Array<RosterDayMeta | null>): RosterCalendarWeek {
  const first = days.find((d) => d !== null);
  const last = [...days].reverse().find((d) => d !== null);
  const label =
    first && last
      ? first.date === last.date
        ? formatDayShort(first.date)
        : `${formatDayShort(first.date)} – ${formatDayShort(last.date)}`
      : `Week ${index + 1}`;

  return { index, label, days };
}

function formatDayShort(iso: string): string {
  const d = new Date(iso + "T12:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Week index that contains `isoDate`, or 0. */
export function weekIndexForDate(weeks: RosterCalendarWeek[], isoDate: string): number {
  const hit = weeks.findIndex((w) => w.days.some((d) => d?.date === isoDate));
  return hit >= 0 ? hit : 0;
}

const SHIFT_PALETTE = [
  "bg-sky-500/20 text-sky-900 dark:text-sky-200 border-sky-500/30",
  "bg-violet-500/20 text-violet-900 dark:text-violet-200 border-violet-500/30",
  "bg-emerald-500/20 text-emerald-900 dark:text-emerald-200 border-emerald-500/30",
  "bg-amber-500/20 text-amber-900 dark:text-amber-200 border-amber-500/30",
  "bg-rose-500/20 text-rose-900 dark:text-rose-200 border-rose-500/30",
  "bg-cyan-500/20 text-cyan-900 dark:text-cyan-200 border-cyan-500/30",
  "bg-fuchsia-500/20 text-fuchsia-900 dark:text-fuchsia-200 border-fuchsia-500/30",
  "bg-lime-500/20 text-lime-900 dark:text-lime-200 border-lime-500/30",
] as const;

const SHIFT_LETTER = [
  "text-sky-600 dark:text-sky-400",
  "text-violet-600 dark:text-violet-400",
  "text-emerald-600 dark:text-emerald-400",
  "text-amber-600 dark:text-amber-400",
  "text-rose-600 dark:text-rose-400",
  "text-cyan-600 dark:text-cyan-400",
  "text-fuchsia-600 dark:text-fuchsia-400",
  "text-lime-700 dark:text-lime-400",
] as const;

function shiftIndex(shiftId: string | null | undefined): number {
  if (!shiftId) return 0;
  const n = Math.abs(Number(shiftId));
  if (!Number.isNaN(n) && n > 0) return n;
  return shiftId.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
}

export function shiftColorClass(shiftId: string | null | undefined): string {
  if (!shiftId) return "bg-muted/40 text-muted-foreground border-border/50";
  return SHIFT_PALETTE[shiftIndex(shiftId) % SHIFT_PALETTE.length];
}

/** Colored letter only (M, E, N…) — card stays neutral. */
export function shiftLetterColorClass(shiftId: string | null | undefined): string {
  if (!shiftId) return "text-muted-foreground";
  return SHIFT_LETTER[shiftIndex(shiftId) % SHIFT_LETTER.length];
}

export function shiftAbbreviation(name: string | null | undefined): string {
  const n = (name ?? "").trim();
  if (!n) return "—";
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return n.slice(0, 3).toUpperCase();
}

/** Single roster cell letter: shift first char (M/E/N…) or O for off days. */
export function rosterCellLetter(
  cell: { workShiftId: string | null; workShiftName: string | null; source?: RosterCellSource },
  day: { dayType: string },
  shiftOptions?: Array<{ id: string; name: string }>
): string {
  if (day.dayType === "holiday") return "O";
  if (cell.source === "employee_off") return "O";
  if (!cell.workShiftId) return "O";

  const name =
    cell.workShiftName?.trim() ||
    shiftOptions?.find((s) => s.id === cell.workShiftId)?.name?.trim() ||
    "";
  if (!name) return "O";
  return name.charAt(0).toUpperCase();
}

export function shiftLegendLetter(name: string): string {
  const n = name.trim();
  return n ? n.charAt(0).toUpperCase() : "?";
}
